import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { BlueprintGrid } from '../components/BlueprintGrid';
import { BrandRays } from '../components/BrandRays';
export function About() {
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const stagger = {
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <main className="w-full" data-id="element-200" data-mp="2s93cabov9cy7">
      {/* HERO */}
      <section className="relative py-24 px-6 bg-paper pt-32 overflow-hidden" data-id="element-201" data-mp="2schbjb28h61f">
        <div className="absolute inset-0 text-seaglass/[0.15]" data-id="element-202" data-mp="2x7d90qq8ollx">
          <BlueprintGrid data-id="element-203" data-mp="1gtwrsyovbodr" />
        </div>

        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-4xl mx-auto relative z-10" data-id="element-204" data-mp="1gx2k0mssn6dp">
          <motion.p variants={fadeIn} className="eyebrow mb-4 text-seaglass" data-id="element-205" data-mp="1uil53ldfakl1">
            About John
          </motion.p>

          <motion.h1 variants={fadeIn} className="text-4xl md:text-6xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-206" data-mp="1pkjnl4vnatbo">
            <span className="text-[#5A6B7C]" data-id="element-207" data-mp="1yic0y3ht3qwf">I built this perspective</span>{' '}
            from inside the revenue journey.
          </motion.h1>

          <motion.div variants={fadeIn} className="text-lg text-text max-w-2xl mb-8 space-y-4" data-id="element-208" data-mp="2deb5x59mdaxc">
            <p data-id="element-209" data-mp="2m9hegc09y73k">
              For 15+ years, I worked inside the parts of the business where
              revenue gets created, converted, delivered, retained, and
              expanded.
            </p>
            <p data-id="element-210" data-mp="2w6ce4y8aadjf">I kept seeing the same pattern.</p>
            <p data-id="element-211" data-mp="2w51b50mnx9td">
              Growth was happening. Demand was being created. Customers were
              still buying.
            </p>
            <p className="font-medium text-navy" data-id="element-212" data-mp="1ov6adj7iip3u">
              But too much revenue was getting stuck, lost, delayed, or missed
              between teams, stages, and decisions.
            </p>
            <p data-id="element-213" data-mp="1xvkuif8qpy5r">That is where this work comes from.</p>
          </motion.div>

          {/* Hero CTAs */}
          <motion.div variants={fadeIn} className="flex flex-col sm:flex-row sm:items-start gap-6 mb-10" data-id="element-214" data-mp="22mkvbvkzd0dj">
            <div className="flex flex-col" data-id="element-215" data-mp="27kma44bdidj7">
              <Link to="/honest-conversation" className="inline-flex items-center space-x-2 px-8 py-4 bg-sand border-2 border-sand text-navy font-medium hover:bg-white transition-all duration-300" data-id="element-216" data-mp="2cjyxcq65pmmb">
                <span data-id="element-217" data-mp="2lkdkzw6lfdsz">Book an Honest Conversation</span>
                <ArrowRight className="w-5 h-5" data-id="element-218" data-mp="m81dv50ecocs" />
              </Link>
              <motion.p variants={fadeIn} className="text-sm text-text-muted mt-2" data-id="element-219" data-mp="m9vwuds3y6e8">
                45 minutes. No pitch. Just clarity.
              </motion.p>
            </div>

            <div className="flex flex-col" data-id="element-220" data-mp="ewwq3nrl5ho8">
              <a href="#proof" className="inline-flex items-center space-x-2 px-6 py-4 border border-navy/20 hover:border-navy/40 text-navy/70 hover:text-navy font-medium transition-all duration-300" data-id="element-221" data-mp="ev25ybo51tl7">
                <span data-id="element-222" data-mp="12lz7cuib58i7">See operating proof</span>
                <ChevronDown className="w-4 h-4" data-id="element-223" data-mp="12lpff1dpcr9v" />
              </a>
              <motion.p variants={fadeIn} className="text-sm text-text-muted mt-2" data-id="element-224" data-mp="xmmik47vlrcz">
                Read the full background below
              </motion.p>
            </div>
          </motion.div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center" animate={{
        y: [0, 6, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-225" data-mp="3des9epm2xm0j">
          <ChevronDown className="w-5 h-5 text-seaglass/50" data-id="element-226" data-mp="15v7efx02x7oz" />
        </motion.div>
      </section>

      {/* WHY I SEE THIS PROBLEM THE WAY I DO */}
      <section className="py-24 px-6 grain border-t hairline-top bg-paper-dark" data-id="element-227" data-mp="r7megbyi3ola">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-228" data-mp="w5fk93vw6gl1">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-229" data-mp="1atsg80uzbzfj">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-230" data-mp="1atireglo7ybf">
              Why I see this problem the way I do
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-231" data-mp="3viga9hov7kdb">
            <p className="text-body text-text" data-id="element-232" data-mp="3gh0k2c4f6dm">
              I have worked across the front end that creates demand, the middle
              that converts it, and the back end that has to deliver what was
              sold and expand the relationship over time.
            </p>

            <p className="text-body text-navy font-medium" data-id="element-233" data-mp="5ulvxbg6e811">
              That changes how you see a business.
            </p>

            <p className="text-body text-text" data-id="element-234" data-mp="wu38f7pihvs">
              You stop treating sales, delivery, customer success, and
              operations like separate issues.
            </p>

            <p className="text-body text-text" data-id="element-235" data-mp="34d15chah472g">
              You start seeing the full revenue journey — and all the places
              where it breaks, stalls, or no longer fits reality.
            </p>

            <p className="text-body text-text" data-id="element-236" data-mp="3eaf9pij4h5ee">
              That is usually where the real problem sits:
            </p>

            <ul className="space-y-3 pl-1" data-id="element-237" data-mp="2qjjo5359f6fl">
              {['the wrong customers get sold', 'handoffs break under load', 'delivery inherits promises it cannot cleanly support', 'churn starts earlier than leadership thinks', 'expansion opportunities stay invisible', 'leaders cannot trust the signal enough to move quickly'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-238" data-mp="3p23glwkghl9b" data-mpi={i}>
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-239" data-mp="3p3ejzv9zoqzw" />
                  <span className="text-body text-text" data-id="element-240" data-mp="2sde1ge07r6r1">{item}</span>
                </li>)}
            </ul>

            <div className="border-l-2 border-seaglass pl-8 py-2" data-id="element-241" data-mp="31c94xibvi2qn">
              <p className="text-body text-navy font-semibold" data-id="element-242" data-mp="31aocpzd16vvy">
                The bottleneck rarely lives neatly inside one department.
              </p>
              <p className="text-body text-navy font-semibold mt-1" data-id="element-243" data-mp="gmjqh6f30iyh">
                It usually lives in the seams.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* RELEVANT OPERATING PROOF */}
      <section id="proof" className="py-24 px-6 bg-paper" data-id="element-244" data-mp="264yq4p1hj3qe">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-245" data-mp="214uaf18eid18">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-246" data-mp="1rc5an3m7qnlq">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-247" data-mp="1nd5xwwu8pmjq">
              Relevant operating proof
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-248" data-mp="1df9picsot5pb">
            <p className="text-body text-text" data-id="element-249" data-mp="18f590mrs4az3">
              At Tom Ferry, I worked across marketing, revenue operations,
              systems, events, and executive leadership as the business scaled
              from roughly $6M to $84M.
            </p>

            <p className="text-body text-navy font-medium" data-id="element-250" data-mp="32to8shkxs0df">
              The lesson was not just how to grow.
            </p>

            <p className="text-body text-navy font-medium" data-id="element-251" data-mp="2bq55ni3aexj3">
              It was how growth changes the journey.
            </p>

            <p className="text-body text-text" data-id="element-252" data-mp="22w0knfau4gbh">
              What worked at one stage stopped working at the next. What looked
              fine inside one team was creating friction somewhere else. What
              looked like a people issue was often a handoff, packaging,
              visibility, or decision problem underneath it.
            </p>

            <p className="text-body text-text" data-id="element-253" data-mp="1xub45k4ufag0">
              Later, as Chief Growth Officer, the work shifted toward efficiency
              under scale. ARR grew from $60.9M to $73.3M while premium tiers
              added $3.5M in six months.
            </p>

            <p className="text-body text-text" data-id="element-254" data-mp="3ebp3buh22t6h">
              Earlier, in revenue operations, redesigning the motion behind the
              work drove a 24% increase in unit sales and a 117% increase in net
              recurring revenue.
            </p>

            <p className="text-body text-text" data-id="element-255" data-mp="12nahajz28so3">
              At Jon Gordon Companies, I served as President, translating
              founder vision into workflows, priorities, and operating structure
              the team could actually run.
            </p>

            <p className="text-body text-text" data-id="element-256" data-mp="1bmnu9mme7zdk">
              That sharpened one of my strongest beliefs:
            </p>

            <div className="border-l-2 border-seaglass pl-8 py-2" data-id="element-257" data-mp="1qeprt00zqgaf">
              <p className="text-body text-navy font-semibold" data-id="element-258" data-mp="1lda2gvrs5nm8">
                If the solution still depends on me sitting in the middle, it is
                not done.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* WHAT I ACTUALLY HELP FIX */}
      <section className="py-24 px-6 bg-paper-dark grain" data-id="element-259" data-mp="4zsctlcfmcpw">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-260" data-mp="18oxfea2quw">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-261" data-mp="37h0txlvfy9hq">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6 leading-tight" data-id="element-262" data-mp="33ecj58n62si4">
              What I actually help fix
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6" data-id="element-263" data-mp="3v50h6btjnhrk">
            <p className="text-body text-text" data-id="element-264" data-mp="3v6bmig2n0xks">
              I am usually not brought in because the company needs more ideas.
            </p>

            <p className="text-body text-text" data-id="element-265" data-mp="3mbdpoxwdd0o6">
              I am brought in because growth is happening, but too much revenue
              is still getting stuck, lost, delayed, or missed along the way.
            </p>

            <p className="text-body text-text" data-id="element-266" data-mp="ion5v0243erj">That often means:</p>

            <ul className="space-y-3 pl-1" data-id="element-267" data-mp="dw3so47c6ln9">
              {['the wrong customers are getting through', 'handoffs are breaking between teams', 'pricing or packaging no longer fits the customer path', 'delivery is cleaning up problems created earlier', 'churn is starting upstream', 'expansion opportunities are not being surfaced', 'leaders cannot get clean enough signal to see what matters', 'too much still depends on interpretation, escalation, and rescue'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-268" data-mp="1945zjyc45091" data-mpi={i}>
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-269" data-mp="1dvnuo5rgg6m3" />
                  <span className="text-body text-text" data-id="element-270" data-mp="1ivs8tp4w4p95">{item}</span>
                </li>)}
            </ul>

            <p className="text-body text-navy font-medium mt-6" data-id="element-271" data-mp="1mxx4etewfvyf">
              My job is to help find the real source of that friction or missed
              upside, then redesign the right part in the right order.
            </p>

            <p className="text-body text-navy font-medium" data-id="element-272" data-mp="3nx0m626we4bi">
              That is how more of the right revenue converts, sticks, and
              expands.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* HOW I WORK */}
      <section className="py-24 px-6 bg-paper" data-id="element-273" data-mp="3sx547wngf73u">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-4xl mx-auto" data-id="element-274" data-mp="3swd7gii4f3bz">
          <motion.div variants={fadeIn} className="mb-12" data-id="element-275" data-mp="kimnz7nd0l95">
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy mb-6" data-id="element-276" data-mp="fja50xzlzybu">
              How I work
            </h2>
          </motion.div>

          <motion.div variants={fadeIn} className="space-y-6 text-body" data-id="element-277" data-mp="2maj1gmt3856k">
            <p className="text-text" data-id="element-278" data-mp="2hbg65u2wtdbs">A few principles guide the work:</p>

            <ul className="space-y-3 pl-1" data-id="element-279" data-mp="wsm2qe4akmsw">
              {['start with the real issue', 'look across the whole journey', 'fix the right thing first', 'use AI where it creates leverage', 'leave the team less dependent'].map((item, i) => <li key={i} className="flex items-start space-x-3" data-id="element-280" data-mp="s0ueia8lyt5x" data-mpi={i}>
                  <span className="w-1.5 h-1.5 rounded-full bg-seaglass flex-shrink-0 mt-2.5" data-id="element-281" data-mp="e2e5udr6qsl5" />
                  <span className="text-body text-text" data-id="element-282" data-mp="j0xvao4zmmi9">{item}</span>
                </li>)}
            </ul>

            <p className="text-body text-text mt-6" data-id="element-283" data-mp="527zdbtwmsvh">
              I am not coming in to run a giant transformation, build management
              theater, or become the long-term operator.
            </p>

            <p className="text-body text-text" data-id="element-284" data-mp="1zgio7kai1n19">
              I am there to help you see what is really happening, make the
              right call, and, if it makes sense, redesign one meaningful part
              of the journey so the business captures more revenue with less
              friction.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* CLOSING CTA */}
      <section className="relative py-24 px-6 grain overflow-hidden bg-paper-dark" data-id="element-285" data-mp="14qxkwmystm1e">
        <div className="absolute right-[-2%] bottom-16 opacity-[0.06] pointer-events-none hidden lg:block" data-id="element-286" data-mp="ztxdo8q7qd8p">
          <BrandRays origin="bottom-right" rayCount={4} size={340} data-id="element-287" data-mp="vslvgo9o4uaj" />
        </div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-6xl mx-auto" data-id="element-288" data-mp="r0kdclptk9cl">
          <div className="grid lg:grid-cols-[5fr_7fr] gap-0 items-stretch" data-id="element-289" data-mp="h4h99abeq0n0">
            {/* Left: Photo */}
            <motion.div variants={fadeIn} className="relative flex items-stretch lg:pr-16 mb-12 lg:mb-0" data-id="element-290" data-mp="c5o0fv72ext6">
              <div className="absolute top-6 left-0 w-[85%] h-[92%] bg-seaglass/10 border border-seaglass/15" data-id="element-291" data-mp="2jq392vz2ufmf" />
              <div className="absolute bottom-0 right-12 w-16 h-1 bg-sand/60" data-id="element-292" data-mp="2nplqvivlqal1" />

              <div className="relative z-10 ml-6 mt-6 w-full" data-id="element-293" data-mp="2spq7ra0f97bs">
                <div className="relative" data-id="element-294" data-mp="2so6orbndsi6b">
                  <div className="absolute -inset-[6px] border border-seaglass/25" data-id="element-295" data-mp="2xo1hlkqpvhu0" />
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/nmtzzfh8cozmkaychcuzkb/a0ec6fac-7fa4-4bbc-a56f-9030015af40e/Westley_Family_25_stevecowellphoto-178.webp" alt="John Westley" className="w-full aspect-[3/4] object-cover object-top grayscale block" data-id="element-296" data-mp="201uc88ml9lgl" />
                  <div className="absolute bottom-4 right-4 w-8 h-8 border-b border-r border-sand/50" data-id="element-297" data-mp="1hauiyv829phi" />
                  <div className="absolute top-4 left-4 w-8 h-8 border-t border-l border-seaglass/40" data-id="element-298" data-mp="1m9nz6veqfuld" />
                </div>
              </div>
            </motion.div>

            {/* Right: CTA */}
            <motion.div variants={fadeIn} className="flex flex-col justify-center lg:pl-4 lg:border-l lg:border-seaglass/15" data-id="element-299" data-mp="1a7wdtzgxsgkv">
              <div className="mb-8" data-id="element-300" data-mp="1o15k3r9l8lz6">
                <div className="w-12 h-px bg-sand mb-5" data-id="element-301" data-mp="1izg7462t1y8g" />
                <p className="eyebrow text-seaglass tracking-widest" data-id="element-302" data-mp="1j0q5kwdkl8fm">
                  Start here
                </p>
              </div>

              <h2 className="text-4xl md:text-5xl font-serif font-semibold text-navy leading-tight mb-6" data-id="element-303" data-mp="r921sy8uxu8e">
                If growth is working but too much revenue is still getting
                stuck, lost, delayed, or missed along the way, start here.
              </h2>

              <div className="space-y-3" data-id="element-304" data-mp="10btd8y1qgq01">
                <Link to="/honest-conversation" className="group inline-flex items-center justify-between w-full sm:w-auto sm:min-w-[340px] px-8 py-5 bg-navy hover:bg-sand text-white hover:text-navy font-semibold transition-all duration-300 border border-navy hover:border-sand" data-id="element-305" data-mp="2gmnlcm0keznv">
                  <span data-id="element-306" data-mp="2h8y29i3lvb1">Book an Honest Conversation</span>
                  <ArrowRight className="w-5 h-5 ml-4 group-hover:translate-x-1 transition-transform duration-300" data-id="element-307" data-mp="3kmc6o479wpg7" />
                </Link>
                <p className="text-sm text-text-muted" data-id="element-308" data-mp="3pkx4xy2ysz20">
                  45 minutes. No pitch. Just clarity.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>
    </main>;
}